import { createContext, useMemo, useContext } from "react"
import { compact, chain, omitBy, isUndefined, omit } from "lodash"
import { Brand } from "helpers/types/helpers"
import { track } from "helpers/analytics"

export type IAnalyticsContext = Brand<object, "AnalyticsContext">
export type EventName = Brand<string, "EventName">
export type TrackingProperties = Record<string, unknown>

/**
 * Analytics context properties including system metadata
 */
export interface AnalyticsContextProperties extends TrackingProperties {
  contextPrefixes?: string[]
}

export interface Analytics {
  track: (event: EventName | string, payload?: TrackingProperties) => void
  contextProperties: AnalyticsContextProperties
}

const EMPTY_ANALYTICS: Analytics = {
  track: (event: EventName | string, payload: TrackingProperties = {}) => track(event, payload),
  contextProperties: {},
}

const AnalyticsContext = createContext<Analytics>(EMPTY_ANALYTICS)

export default AnalyticsContext

/**
 * Checks if analytics debug logging is enabled.
 * Safe to call in any environment (SSR, private browsing, etc.)
 */
function isDebugLoggingEnabled(): boolean {
  if (process.env.NODE_ENV !== "development") return false

  try {
    const localStorageEnabled = typeof localStorage !== "undefined"
      && localStorage.getItem("DEBUG_ANALYTICS") === "true"
    const windowFlagEnabled = typeof window !== "undefined"
      && (window as Window & { __DEBUG_ANALYTICS__?: boolean }).__DEBUG_ANALYTICS__ === true

    return localStorageEnabled || windowFlagEnabled
  } catch {
    // localStorage access can fail in private browsing or SSR
    return false
  }
}

/**
 * Creates an analytics instance with custom properties and optional prefix.
 * Inherits from parent analytics context to build nested context hierarchies.
 *
 * Use this when you need analytics but can't use a provider (rare edge cases).
 * Most components should use a provider + useAnalytics() instead.
 *
 * @param contextProperties - Properties to automatically include in all tracking calls
 * @param contextPrefix - Prefix to add to event names (e.g., "Dashboard")
 * @returns Analytics object with track function
 */
export function useAnalyticsWithProperties(
  contextProperties: TrackingProperties = {},
  contextPrefix?: string,
): Analytics {
  const parentAnalytics = useContext(AnalyticsContext)

  const analyticsContextValue = useMemo(() => {
    const parentContextProperties = parentAnalytics.contextProperties
    const parentPrefixes = parentContextProperties.contextPrefixes ?? []

    const contextPrefixes = compact([...parentPrefixes, contextPrefix])

    // Filter out undefined values to prevent overriding defined parent values
    const definedContextProperties = omitBy(contextProperties, isUndefined)

    const mergedContextProperties: AnalyticsContextProperties = {
      ...parentContextProperties,
      ...definedContextProperties,
      ...(contextPrefixes.length > 0 && { contextPrefixes }),
    }

    function trackEvent(event: EventName | string, payload: TrackingProperties = {}) {
      // contextPrefixes is structural metadata for building event names, not a Segment property.
      const finalPayload = {
        ...omit(mergedContextProperties, "contextPrefixes"),
        ...payload,
      }

      // Check if analytics logging is enabled
      // Toggle from console: localStorage.setItem('DEBUG_ANALYTICS', 'true') or localStorage.removeItem('DEBUG_ANALYTICS')
      if (isDebugLoggingEnabled()) {
        const prefixedEventForLogging = chain([...contextPrefixes, event])
          .compact()
          .join(" - ")
          .value()

        console.group(prefixedEventForLogging)
        console.log("Context Prefixes:", contextPrefixes)
        console.log("Parent Context Properties:", parentContextProperties)
        console.log("Current Context Properties (defined only):", definedContextProperties)
        console.log("Merged Context Properties:", mergedContextProperties)
        console.log("Event Payload:", payload)
        console.log("Final Payload:", finalPayload)
        console.groupEnd()
      }

      track(event, finalPayload)
    }

    return {
      track: trackEvent,
      contextProperties: mergedContextProperties,
    }
  }, [contextProperties, contextPrefix, parentAnalytics])

  return analyticsContextValue
}

export function useAnalytics(): Analytics {
  return useContext(AnalyticsContext)
}
